import React, { useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { FaChevronRight, FaTimes, FaBullhorn, FaClock, FaUserEdit } from 'react-icons/fa';
import { playerAPI } from '../services/api';

const BRAND = {
  blue: '#043663',
  red: '#D52B1E',
};

const VISIBLE_CAP = 3;
const AVAILABILITY_SORT = 300;

const StackRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  margin-bottom: 1.15rem;
`;

const BannerCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 1rem 1.15rem;
  border-radius: 14px;
  border: ${(p) => p.$border};
  background: ${(p) => p.$bg};
  box-shadow:
    0 2px 10px rgba(15, 23, 42, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.85);
  position: relative;
`;

const BannerIcon = styled.div`
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 11px;
  background: ${(p) => p.$iconBg};
  color: ${(p) => p.$iconColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
`;

const BannerBody = styled.div`
  flex: 1;
  min-width: 0;
  padding-right: ${(p) => (p.$dismissible ? '2rem' : '0')};
`;

const BannerTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
  margin-bottom: 0.35rem;
`;

const BannerText = styled.p`
  margin: 0 0 0.65rem 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #475569;
  white-space: pre-wrap;
`;

const BannerCta = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: ${BRAND.blue};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${BRAND.blue};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const DismissBtn = styled.button`
  position: absolute;
  top: 0.65rem;
  right: 0.65rem;
  border: none;
  background: rgba(15, 23, 42, 0.06);
  border-radius: 8px;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #475569;
  &:hover {
    background: rgba(15, 23, 42, 0.1);
    color: #0f172a;
  }
  &:focus-visible {
    outline: 2px solid ${BRAND.blue};
    outline-offset: 2px;
  }
`;

const MoreHint = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: #64748b;
  padding-left: 0.15rem;
`;

function variantShell(variant) {
  const v = String(variant || 'info').toLowerCase();
  if (v === 'urgent') {
    return {
      border: '1px solid rgba(213, 43, 30, 0.28)',
      bg: 'linear-gradient(145deg, #fff8f7 0%, #ffffff 42%, #fff5f5 100%)',
      iconBg: 'rgba(213, 43, 30, 0.12)',
      iconColor: BRAND.red,
    };
  }
  if (v === 'warning') {
    return {
      border: '1px solid rgba(217, 119, 6, 0.35)',
      bg: 'linear-gradient(145deg, #fffbeb 0%, #ffffff 45%, #fff7ed 100%)',
      iconBg: 'rgba(217, 119, 6, 0.15)',
      iconColor: '#d97706',
    };
  }
  return {
    border: '1px solid rgba(4, 54, 99, 0.22)',
    bg: 'linear-gradient(145deg, #f0f9ff 0%, #ffffff 45%, #eff6ff 100%)',
    iconBg: 'rgba(4, 54, 99, 0.12)',
    iconColor: BRAND.blue,
  };
}

function safeInAppPath(path) {
  if (!path || typeof path !== 'string') return null;
  const p = path.trim();
  if (!p.startsWith('/player/') && p !== '/player') return null;
  return p;
}

/**
 * @param {{ availabilityReminder: null | { variant: 'never' | 'new_week' }; fetchEnabled?: boolean }} props
 */
export default function PlayerHomeBannerStack({ availabilityReminder = null, fetchEnabled = true }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery(
    ['playerHomeBanners'],
    () => playerAPI.getHomeBanners(),
    {
      enabled: fetchEnabled,
      staleTime: 45 * 1000,
      refetchOnWindowFocus: true,
      retry: 1,
    }
  );

  const dismissMut = useMutation((bannerId) => playerAPI.dismissHomeBanner(bannerId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['playerHomeBanners']);
      toast.success(t('playerDashboardPage.homeBannerDismissed'));
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t('playerDashboardPage.homeBannerDismissFailed'));
    },
  });

  const merged = useMemo(() => {
    const apiItems = data?.success === false ? [] : data?.data?.items || [];
    const list = Array.isArray(apiItems) ? [...apiItems] : [];

    if (availabilityReminder?.variant) {
      list.push({
        id: '__availability',
        source: 'system',
        kind: 'availability',
        sort_key: AVAILABILITY_SORT,
        variant: 'warning',
        availabilityVariant: availabilityReminder.variant,
      });
    }

    list.sort((a, b) => (b.sort_key || 0) - (a.sort_key || 0));
    return list;
  }, [data, availabilityReminder]);

  const visible = useMemo(() => merged.slice(0, VISIBLE_CAP), [merged]);
  const hiddenCount = merged.length - visible.length;

  const onDismiss = useCallback(
    (id) => {
      if (!id || dismissMut.isLoading) return;
      dismissMut.mutate(id);
    },
    [dismissMut]
  );

  if (isLoading && merged.length === 0 && !availabilityReminder) {
    return null;
  }
  if (isError && merged.length === 0 && !availabilityReminder) {
    return null;
  }
  if (merged.length === 0) {
    return null;
  }

  return (
    <StackRoot role="status" aria-live="polite" aria-relevant="additions text">
      {visible.map((item) => {
        const shell = variantShell(item.variant);

        if (item.kind === 'availability') {
          return (
            <BannerCard key="__availability" $border={shell.border} $bg={shell.bg}>
              <BannerIcon $iconBg="rgba(213, 43, 30, 0.12)" $iconColor={BRAND.red}>
                <FaClock aria-hidden />
              </BannerIcon>
              <BannerBody $dismissible={false}>
                <BannerTitle>{t('playerDashboardPage.availabilityReminderTitle')}</BannerTitle>
                <BannerText>
                  {item.availabilityVariant === 'never'
                    ? t('playerDashboardPage.availabilityReminderNever')
                    : t('playerDashboardPage.availabilityReminderNewWeek')}
                </BannerText>
                <BannerCta to="/player/settings/availability">
                  {t('playerDashboardPage.availabilityReminderCta')} <FaChevronRight size={10} aria-hidden />
                </BannerCta>
              </BannerBody>
            </BannerCard>
          );
        }

        if (item.kind === 'profile_incomplete') {
          const ctaPath = safeInAppPath(item.cta_path) || '/player/settings/profile';
          return (
            <BannerCard key="__profile" $border={shell.border} $bg={shell.bg}>
              <BannerIcon $iconBg={shell.iconBg} $iconColor={shell.iconColor}>
                <FaUserEdit aria-hidden />
              </BannerIcon>
              <BannerBody $dismissible={false}>
                <BannerTitle>{t('playerDashboardPage.homeBannerProfileTitle')}</BannerTitle>
                <BannerText>{t('playerDashboardPage.homeBannerProfileBody')}</BannerText>
                <BannerCta to={ctaPath}>
                  {t('playerDashboardPage.homeBannerProfileCta')} <FaChevronRight size={10} aria-hidden />
                </BannerCta>
              </BannerBody>
            </BannerCard>
          );
        }

        const title = item.title || t('playerDashboardPage.homeBannerFallbackTitle');
        const body = item.body || '';
        const ctaPath = safeInAppPath(item.cta_path);
        const ctaLabel = item.cta_label;

        return (
          <BannerCard key={item.id || 'unknown'} $border={shell.border} $bg={shell.bg}>
            {item.dismissible && item.id ? (
              <DismissBtn
                type="button"
                aria-label={t('playerDashboardPage.homeBannerDismissAria')}
                onClick={() => onDismiss(item.id)}
                disabled={dismissMut.isLoading}
              >
                <FaTimes aria-hidden />
              </DismissBtn>
            ) : null}
            <BannerIcon $iconBg={shell.iconBg} $iconColor={shell.iconColor}>
              <FaBullhorn aria-hidden />
            </BannerIcon>
            <BannerBody $dismissible={!!item.dismissible}>
              <BannerTitle>{title}</BannerTitle>
              {body ? <BannerText>{body}</BannerText> : null}
              {ctaPath && ctaLabel ? (
                <BannerCta to={ctaPath}>
                  {ctaLabel} <FaChevronRight size={10} aria-hidden />
                </BannerCta>
              ) : null}
            </BannerBody>
          </BannerCard>
        );
      })}
      {hiddenCount > 0 ? (
        <MoreHint>{t('playerDashboardPage.homeBannerMoreHidden', { count: hiddenCount })}</MoreHint>
      ) : null}
    </StackRoot>
  );
}
